import { RequestStatus } from 'features/request/requestSlice';
import { useRequest } from 'features/request/useRequest';
import { useResetStoreData } from 'features/request/useResetStoreData';
import type {
    IUseIncludeExcludeWorkOrderObjectsDataProps,
    IUseIncludeExcludeWorkOrderObjectsDataResult,
} from 'pages/Project/Common/WorkOrderStructure/types';
import { WorkOrderStructureOperationType } from 'pages/Project/Common/WorkOrderStructure/types';

import { useCallback, useEffect, useMemo } from 'react';
import { useDispatch } from 'react-redux';

import {
    uerExcludeContractObjectFromWorkOrderGql,
    uerIncludeContractObjectToWorkOrderGql,
} from '../../workOrderQueries';

const includeRequestId = 'uerIncludeContractObjectToWorkOrder';
const excludeRequestId = 'uerExcludeContractObjectFromWorkOrder';

export const useIncludeExcludeUerWorkOrderObjectsData = (
    props: IUseIncludeExcludeWorkOrderObjectsDataProps,
): IUseIncludeExcludeWorkOrderObjectsDataResult => {
    const dispatch = useDispatch();

    const { workOrderOrChangeId, onSuccess } = props;

    const [uerIncludeContractObjectToWorkOrderReset] = useResetStoreData({ requestId: includeRequestId });
    const [uerExcludeContractObjectFromWorkOrderReset] = useResetStoreData({ requestId: excludeRequestId });

    const [
        uerIncludeContractObjectToWorkOrderData,
        uerIncludeContractObjectToWorkOrderStatus,
        ,
        uerIncludeContractObjectToWorkOrderRequest,
    ] = useRequest({
        requestId: includeRequestId,
        query: uerIncludeContractObjectToWorkOrderGql,
        withEffect: false,
    });

    const [
        uerExcludeContractObjectFromWorkOrderData,
        uerExcludeContractObjectFromWorkOrderStatus,
        ,
        uerExcludeContractObjectFromWorkOrderRequest,
    ] = useRequest({
        requestId: excludeRequestId,
        query: uerExcludeContractObjectFromWorkOrderGql,
        withEffect: false,
    });

    useEffect(() => {
        uerExcludeContractObjectFromWorkOrderReset(undefined);
        uerIncludeContractObjectToWorkOrderReset(undefined);
    }, [uerExcludeContractObjectFromWorkOrderReset, uerIncludeContractObjectToWorkOrderReset]);

    useEffect(() => {
        if (uerIncludeContractObjectToWorkOrderStatus === RequestStatus.SUCCESS) {
            onSuccess(
                WorkOrderStructureOperationType.Include,
                uerIncludeContractObjectToWorkOrderData?.uerIncludeContractObjectToWorkOrder?.data || [],
            );
            uerIncludeContractObjectToWorkOrderReset(undefined);
            uerExcludeContractObjectFromWorkOrderReset(undefined);
        }

        if (uerExcludeContractObjectFromWorkOrderStatus === RequestStatus.SUCCESS) {
            onSuccess(
                WorkOrderStructureOperationType.Exclude,
                uerExcludeContractObjectFromWorkOrderData?.uerExcludeContractObjectFromWorkOrder?.data || [],
            );
            uerIncludeContractObjectToWorkOrderReset(undefined);
            uerExcludeContractObjectFromWorkOrderReset(undefined);
        }
    }, [
        onSuccess,
        uerExcludeContractObjectFromWorkOrderData?.uerExcludeContractObjectFromWorkOrder?.data,
        uerExcludeContractObjectFromWorkOrderReset,
        uerExcludeContractObjectFromWorkOrderStatus,
        uerIncludeContractObjectToWorkOrderData?.uerIncludeContractObjectToWorkOrder?.data,
        uerIncludeContractObjectToWorkOrderReset,
        uerIncludeContractObjectToWorkOrderStatus,
    ]);

    const makeRequest = useCallback(
        (operation: WorkOrderStructureOperationType, contractObjectId: number | undefined) => {
            uerExcludeContractObjectFromWorkOrderReset(undefined);
            uerIncludeContractObjectToWorkOrderReset(undefined);

            if (operation === WorkOrderStructureOperationType.Include) {
                dispatch(
                    uerIncludeContractObjectToWorkOrderRequest({ workOrderId: workOrderOrChangeId, contractObjectId }),
                );
            }

            if (operation === WorkOrderStructureOperationType.Exclude) {
                dispatch(
                    uerExcludeContractObjectFromWorkOrderRequest({
                        workOrderId: workOrderOrChangeId,
                        contractObjectId,
                    }),
                );
            }
        },
        [
            dispatch,
            uerExcludeContractObjectFromWorkOrderRequest,
            uerExcludeContractObjectFromWorkOrderReset,
            uerIncludeContractObjectToWorkOrderRequest,
            uerIncludeContractObjectToWorkOrderReset,
            workOrderOrChangeId,
        ],
    );

    const status = useMemo(
        () => uerIncludeContractObjectToWorkOrderStatus || uerExcludeContractObjectFromWorkOrderStatus,
        [uerExcludeContractObjectFromWorkOrderStatus, uerIncludeContractObjectToWorkOrderStatus],
    );

    return {
        makeRequest,
        status,
    };
};
